<template>
  <FormularioCadastro
    module-name="Tributo"
    :is-loading="isLoading"
    :is-editing="isEditing"
    :entity-info="entityInfo"
    @submit="salvar"
    @submit-continuar="salvarAlteracoes"
    @cancelar="handleCancel"
    @show-notification="(p) => emit('show-notification', p)"
    :ocultar-salvar="!podeEditar"
  >
    <template #acoes-header>
      <button
        v-if="isEditing"
        type="button"
        class="btn-icon-minimalista me-3"
        title="Ver Histórico de Alterações"
        @click="mostrarAuditoria = true"
      >
        <i class="bi bi-clock-history"></i>
      </button>
    </template>

    <div v-if="!podeEditar" class="alert alert-warning border-0 rounded-3 mb-4 d-flex align-items-center">
      <i class="bi bi-lock-fill fs-4 me-3 text-warning"></i>
      <div><strong>Modo de Leitura:</strong> O seu perfil não tem permissões para editar este registo.</div>
    </div>

    <!-- ============================================================ -->
    <!-- FRAME: DADOS DO TRIBUTO                                       -->
    <!-- ============================================================ -->
    <div class="card mb-4 shadow-sm border-0">
      <div class="card-header bg-transparent border-bottom-0 pb-0 pt-3">
        <h6 class="mb-0 text-body-emphasis"><i class="bi bi-receipt me-2"></i>Dados do Tributo</h6>
      </div>
      <div class="card-body">
        <div class="row g-3 align-items-top">
          <div class="col-md-6">
            <CampoBuscaLista
              id="lstTipoTributo"
              label="Tipo de Tributo"
              v-model="item.lstTipoTributo"
              tabela="TributoConfiguracao"
              required
              :disabled="!podeEditar"
              dica="Tributo brasileiro ao qual esta configuração se refere (ex: CBS, IBS, ICMS Estadual, DIFAL)."
            />
          </div>
          <div class="col-md-6">
            <CampoBuscaLista
              id="lstMomentoGeracao"
              label="Momento de Geração"
              v-model="item.lstMomentoGeracao"
              tabela="TributoConfiguracao"
              required
              :disabled="!podeEditar"
              dica="Momento em que o tributo é efetivamente gerado no sistema (ex: Faturamento, Liquidação)."
            />
          </div>
        </div>
        <div class="row g-3 mt-1">
          <div class="col-md-4">
            <CampoInput
              id="dataInicioVigencia"
              label="Data de Início (Vigência)"
              type="date"
              v-model="item.dataInicioVigencia"
              placeholder="Opcional (Em branco = Sem vigência definida)"
              :disabled="!podeEditar"
            />
          </div>
          <div class="col-md-4">
            <CampoInput
              id="dataFimVigencia"
              label="Data de Término (Vigência)"
              type="date"
              v-model="item.dataFimVigencia"
              placeholder="Opcional (Em branco = Indeterminado)"
              :disabled="!podeEditar"
            />
          </div>
        </div>
        <div class="row g-3 mt-1">
          <div class="col-12">
            <div class="alert alert-info border-0 rounded-3 mb-0 d-flex align-items-center">
              <i class="bi bi-info-circle fs-5 me-3 text-info"></i>
              <div class="small">
                Não é permitido cadastrar dois tributos com o <strong>mesmo Tipo</strong> e <strong>mesmo Momento de Geração</strong> com períodos de vigência sobrepostos.
                Deixe as datas em branco para representar vigência indeterminada.
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </FormularioCadastro>

  <AuditLogViewer
    v-if="mostrarAuditoria"
    @fechar="mostrarAuditoria = false"
    tabela="TributoConfiguracao"
    contexto="tenant"
    :chave-primaria="item.idTributoConfiguracao"
    :titulo-registro="tituloRegistro"
  />
</template>

<script setup>
import { ref, computed } from 'vue'
import api from '@/services/api'
import { useFormulario } from '@/composables/useFormulario'
import { useAuthStore } from '@/stores/auth'
import FormularioCadastro from '@/components/base/formulario/FormularioCadastro.vue'
import CampoInput from '@/components/base/campos/CampoInput.vue'
import CampoBuscaLista from '@/components/base/campos/CampoBuscaLista.vue'
import AuditLogViewer from '@/components/base/auditoria/AuditLogViewer.vue'

const props = defineProps({
  itemParaEditar: { type: Object, default: null },
  modeloVazio: {
    type: Object,
    default: () => ({
      idTributoConfiguracao: null,
      lstTipoTributo: '',
      lstMomentoGeracao: '',
      dataInicioVigencia: null,
      dataFimVigencia: null
    })
  }
})

const emit = defineEmits(['itemAtualizado', 'cancelar', 'show-notification', 'changesDetected'])

const authStore = useAuthStore()

const podeEditar = computed(() => {
  return authStore.isAdminInterno || (authStore.permissoes && authStore.permissoes.includes('TRIBUTO_CONFIGURACAO_COMPLETO'))
})

const {
  item,
  isLoading,
  isEditing,
  entityInfo,
  submitForm,
  initializeForm,
  handleCancel
} = useFormulario(props, emit)

const mostrarAuditoria = ref(false)

const tituloRegistro = computed(() => {
  return item.value.lstTipoTributo
    ? `${item.value.lstTipoTributo} / ${item.value.lstMomentoGeracao || ''}`
    : 'Tributo'
})

function montarPayload () {
  return {
    idTributoConfiguracao: item.value.idTributoConfiguracao,
    // GUARDIÃO: Escopo multiorganização (Tenant) — injeta a organização ativa.
    // O backend faz override absoluto via ITenantContext; o envio aqui é defesa em profundidade.
    idOrganizacao: authStore.organizacaoAtiva ? Number(authStore.organizacaoAtiva) : null,
    lstTipoTributo: item.value.lstTipoTributo,
    lstMomentoGeracao: item.value.lstMomentoGeracao,
    dataInicioVigencia: item.value.dataInicioVigencia || null,
    dataFimVigencia: item.value.dataFimVigencia || null,
    xmin: item.value.xmin
  }
}

async function salvar () {
  if (!podeEditar.value) return
  await submitForm('TributosConfiguracao', 'idTributoConfiguracao', montarPayload())
}

async function salvarAlteracoes () {
  if (!podeEditar.value) return

  const payload = montarPayload()

  try {
    isLoading.value = true
    const res = item.value.idTributoConfiguracao
      ? await api.put(`/TributosConfiguracao/${item.value.idTributoConfiguracao}`, payload)
      : await api.post('/TributosConfiguracao', payload)

    if (res?.data) {
      initializeForm(res.data)
    }
    emit('show-notification', { message: 'Alterações salvas com sucesso!', type: 'success' })
  } catch (error) {
    const data = error.response?.data
    let msg = 'Erro ao salvar alterações.'

    if (error.response?.status === 409) {
      msg = data?.message || 'Conflito: outro usuário alterou este registro. Recarregue os dados.'
    } else if (typeof data === 'string') {
      msg = data
    } else if (data?.message) {
      msg = data.message
    } else if (data?.title) {
      msg = data.title
    } else if (data?.errors) {
      msg = Object.values(data.errors).flat().join('; ')
    }

    emit('show-notification', { message: msg, type: 'error' })
  } finally {
    isLoading.value = false
  }
}
</script>

<style scoped>
</style>
